// useGeolocation — thin wrapper around the browser's Geolocation API for
// the scouting feature. Two surfaces:
//
//   getCurrentPositionOnce() — Promise that resolves to the best available
//                              position within the timeout (or null if the
//                              user denied / GPS is unavailable). Used at
//                              observation save time so each scouting row
//                              gets a lat/lng/accuracy_m stamp.
//
//   useScoutWalk()           — React hook for capturing a polyline while
//                              the scout walks the orchard. Internally
//                              uses navigator.geolocation.watchPosition.
//                              Buffers points in a ref + exposes a
//                              minimal state ({active, count, distance})
//                              so re-render cost stays low even on a
//                              long walk with thousands of points.
//
// Mobile + offline brief: the Geolocation API works without network, so
// walks survive signal loss. The eventual save POST goes through the
// offline queue for the same blip-survival reason.

import { useCallback, useEffect, useRef, useState } from 'react'
import { haversineMeters, WALK_MAX_ACCURACY_M } from '../lib/walkTrack'

export interface ScoutPoint {
  lat: number
  lng: number
  t:   number       // epoch ms
  acc: number       // meters (GeolocationCoordinates.accuracy)
}

export interface ScoutPosition {
  lat:           number
  lng:           number
  accuracy_m:    number
  timestamp_ms:  number
}

const DEFAULT_TIMEOUT_MS = 5_000

// Minimal shape of the Screen Wake Lock sentinel — typed locally so we don't
// depend on the DOM lib shipping WakeLock types on every build target.
interface WakeLockSentinelLike {
  release?: () => Promise<void>
  addEventListener?: (type: string, cb: () => void) => void
}

// Single-shot — used at observation save time.
export function getCurrentPositionOnce(timeoutMs: number = DEFAULT_TIMEOUT_MS): Promise<ScoutPosition | null> {
  if (typeof navigator === 'undefined' || !navigator.geolocation) return Promise.resolve(null)
  return new Promise(resolve => {
    const id = setTimeout(() => resolve(null), timeoutMs + 500)
    navigator.geolocation.getCurrentPosition(
      pos => {
        clearTimeout(id)
        resolve({
          lat:          pos.coords.latitude,
          lng:          pos.coords.longitude,
          accuracy_m:   pos.coords.accuracy,
          timestamp_ms: pos.timestamp,
        })
      },
      _err => {
        clearTimeout(id)
        resolve(null)
      },
      { enableHighAccuracy: true, timeout: timeoutMs, maximumAge: 30_000 },
    )
  })
}

// Seed for start() — resume a persisted draft mid-walk: the buffer starts from
// the draft's points so the watch appends to the existing track instead of
// beginning a fresh one (Scouting draft-recovery Resume).
export interface WalkSeed { points: ScoutPoint[]; distanceM: number }

export interface UseGpsTrackResult {
  active:     boolean
  pointCount: number
  distanceM:  number
  // Last position (for live overlay); changes ref-stable enough for rendering.
  last:       ScoutPoint | null
  // Read-only access to the buffered track (do NOT mutate).
  readonly trackRef: React.RefObject<ScoutPoint[]>
  // Permissions / GPS errors surface here.
  error:      string | null
  start:      (seed?: WalkSeed) => void
  stop:       () => ScoutPoint[]
  /**
   * The best accuracy seen while NOTHING is being recorded, in metres.
   *
   * A coarse fix is dropped on purpose (see pushPoint), but silently: a scout
   * under canopy could watch "Walking · 0 pts" for an hour, then lose the walk
   * on Stop to a message blaming its length. This is what lets the banner say
   * the real reason. Null once a point has landed, or before any fix arrives.
   */
  coarseOnlyBestM: number | null
}

// Backwards-compat alias — Scouting page imported `UseScoutWalkResult` first.
export type UseScoutWalkResult = UseGpsTrackResult

// Initial buffer state for start(): empty for a fresh walk, or the seed's
// points (copied) when resuming a draft.
function seedInit(seed: WalkSeed | undefined): { points: ScoutPoint[]; distanceM: number; last: ScoutPoint | null } {
  if (!seed) return { points: [], distanceM: 0, last: null }
  const points = seed.points.slice()
  return { points, distanceM: seed.distanceM, last: points[points.length - 1] ?? null }
}

export function useGpsTrack(): UseGpsTrackResult {
  const [active,     setActive]     = useState(false)
  const [pointCount, setPointCount] = useState(0)
  const [distanceM,  setDistanceM]  = useState(0)
  const [last,       setLast]       = useState<ScoutPoint | null>(null)
  const [error,      setError]      = useState<string | null>(null)
  // Best (smallest) accuracy among fixes REJECTED by the gate, while the track
  // is still empty. Cleared the moment a real point lands.
  const [coarseOnlyBestM, setCoarseOnlyBestM] = useState<number | null>(null)

  const trackRef    = useRef<ScoutPoint[]>([])
  const watcherRef  = useRef<number | null>(null)
  const wakeLockRef = useRef<WakeLockSentinelLike | null>(null)

  // Hold the screen awake while tracking. watchPosition is suspended by the OS
  // once the screen locks / the PWA is backgrounded, which silently truncated
  // real field captures to nothing (spray drives: prod 30 Jun 2026, a 5-hour
  // spray produced zero track points because the operator's phone locked). The
  // Screen Wake Lock API keeps the display on for the duration. Absent on older
  // iOS — capture still works there, just not through a manual screen lock.
  const acquireWakeLock = useCallback(async () => {
    try {
      const wl = (navigator as unknown as { wakeLock?: { request: (t: 'screen') => Promise<WakeLockSentinelLike> } }).wakeLock
      if (!wl || typeof document === 'undefined' || document.visibilityState !== 'visible') return
      if (wakeLockRef.current) return
      const sentinel = await wl.request('screen')
      wakeLockRef.current = sentinel
      // The sentinel auto-releases when the tab is hidden; drop our handle so
      // the visibilitychange effect re-acquires when the operator returns.
      sentinel.addEventListener?.('release', () => { wakeLockRef.current = null })
    } catch { /* denied / unsupported — capture continues without it */ }
  }, [])

  const releaseWakeLock = useCallback(() => {
    try { void wakeLockRef.current?.release?.() } catch { /* ignore */ }
    wakeLockRef.current = null
  }, [])

  const start = useCallback((seed?: WalkSeed) => {
    if (active) return
    if (typeof navigator === 'undefined' || !navigator.geolocation) {
      setError('Geolocation not supported on this device.')
      return
    }
    const init = seedInit(seed)
    trackRef.current = init.points
    setPointCount(init.points.length)
    setDistanceM(init.distanceM)
    setLast(init.last)
    setError(null)
    setCoarseOnlyBestM(null)
    setActive(true)
    void acquireWakeLock()

    // Append a fix to the track, skipping near-duplicates (same spot ±2m
    // within 2s) — they bloat the track without adding info. The watcher
    // fires every ~1s on mobile; orchard scouts walk slowly.
    const pushPoint = (pos: GeolocationPosition) => {
      // Reject coarse fixes outright. When the phone can't get a real GPS lock
      // it falls back to cell-tower / wifi location (accuracy hundreds-to-
      // thousands of metres) and reports a point far from the true path; the
      // next good fix snaps back, drawing a phantom straight-line spike (the
      // 18 Jun 2026 incident). Real orchard fixes are single-digit metres, so
      // this gate keeps every legitimate point. Trade-off: a desktop browser
      // on coarse network location now records nothing until it gets a fix
      // under the threshold — correct, since that guess isn't a walk anyway.
      if (!(pos.coords.accuracy <= WALK_MAX_ACCURACY_M)) {
        // Rejected — but say so. Silence here is what let a scout walk a whole
        // block believing it was recording. Only while nothing has landed yet;
        // once the track has points, a coarse fix is just a skipped sample.
        if (trackRef.current.length === 0) {
          setCoarseOnlyBestM(prev => (prev == null ? pos.coords.accuracy : Math.min(prev, pos.coords.accuracy)))
        }
        return
      }
      const p: ScoutPoint = {
        lat: pos.coords.latitude,
        lng: pos.coords.longitude,
        t:   pos.timestamp,
        acc: pos.coords.accuracy,
      }
      const prev = trackRef.current[trackRef.current.length - 1]
      if (prev && p.t - prev.t < 2_000 && haversineMeters(prev, p) < 2) return
      trackRef.current.push(p)
      setCoarseOnlyBestM(null)
      setPointCount(trackRef.current.length)
      setLast(p)
      if (prev) setDistanceM(d => d + haversineMeters(prev, p))
      // A good fix clears any prior transient GPS message — iOS fires
      // kCLErrorLocationUnknown intermittently between successful reads, so the
      // banner shouldn't linger once tracking resumes. (no-op when already null)
      setError(null)
    }

    // Seed an immediate fix so the walk shows a point right away instead of
    // sitting at "0 points" while watchPosition waits for its first lock.
    // maximumAge accepts a recent cached position (instant on most devices);
    // pushPoint still applies the accuracy gate, so a stale/coarse cached seed
    // is dropped rather than planting a phantom first point far from the start.
    navigator.geolocation.getCurrentPosition(
      pushPoint,
      () => { /* the watcher below reports any persistent failure */ },
      { enableHighAccuracy: true, timeout: 10_000, maximumAge: 60_000 },
    )

    // Continuous watch. The timeout is essential: WITHOUT it, a device that
    // never gets a fresh high-accuracy fix (a desktop browser, or location
    // disabled at the OS level) leaves the walk stuck at "Walking · 0 points"
    // forever with no error. With it, the failure surfaces so the scout can act.
    watcherRef.current = navigator.geolocation.watchPosition(
      pushPoint,
      err => {
        // PERMISSION_DENIED is the only persistent, user-actionable failure —
        // always surface it (and stop here).
        if (err.code === err.PERMISSION_DENIED) {
          setError('Location access denied — allow it for this site (and enable Location Services for your browser), then start the walk again.')
          return
        }
        // POSITION_UNAVAILABLE (iOS surfaces kCLErrorLocationUnknown as
        // "The operation couldn't be completed. (kCLErrorDomain error 0.)") and
        // TIMEOUT are routinely transient — iOS fires them intermittently even
        // while tracking fine, which is why the walk keeps logging points
        // through them. If we already have points, GPS is demonstrably working,
        // so swallow it (a good fix already cleared any prior message). Only
        // when we have NO fix yet do we show a calm, non-alarming hint so a
        // genuinely stuck walk still tells the scout what to check.
        if (trackRef.current.length > 0) return
        setError(
          err.code === err.TIMEOUT
            ? 'Still finding GPS… move somewhere with a clearer view of the sky. If it stays at 0 points, check Location Services for your browser.'
            : 'Still finding GPS… make sure Location Services are on for your browser. If it stays at 0 points, stop and start the walk again.',
        )
      },
      { enableHighAccuracy: true, timeout: 20_000, maximumAge: 1_000 },
    )
  }, [active, acquireWakeLock])

  const stop = useCallback((): ScoutPoint[] => {
    if (watcherRef.current != null && typeof navigator !== 'undefined' && navigator.geolocation) {
      navigator.geolocation.clearWatch(watcherRef.current)
      watcherRef.current = null
    }
    releaseWakeLock()
    setActive(false)
    return trackRef.current.slice()
  }, [releaseWakeLock])

  // Re-acquire the wake lock when the operator returns to a still-active track
  // (the sentinel is auto-released whenever the tab is hidden).
  useEffect(() => {
    if (!active || typeof document === 'undefined') return
    const onVis = () => { if (document.visibilityState === 'visible') void acquireWakeLock() }
    document.addEventListener('visibilitychange', onVis)
    return () => document.removeEventListener('visibilitychange', onVis)
  }, [active, acquireWakeLock])

  // Cleanup on unmount — don't leak the watch handle or wake lock if the user
  // navigates away with a walk active (rare but possible).
  useEffect(() => {
    return () => {
      if (watcherRef.current != null && typeof navigator !== 'undefined' && navigator.geolocation) {
        navigator.geolocation.clearWatch(watcherRef.current)
      }
      releaseWakeLock()
    }
  }, [releaseWakeLock])

  return { active, pointCount, distanceM, last, trackRef, error, coarseOnlyBestM, start, stop }
}

// Backwards-compat alias for the Scouting page.
export const useScoutWalk = useGpsTrack
